import type { BookingPlayerSlotPaymentInput, OnlinePaymentRequirement } from '@boubou/types';
import { onlinePaymentRequirementNeedsCheckout } from '@boubou/types';
import { getApiBaseUrl } from './api';
import { publicBookingApiParamsObjectFromWindow } from '../utils/club-route-key';

const getApiBase = () => getApiBaseUrl();

export type PaymentRequirementsResponse = {
  requirement: OnlinePaymentRequirement;
  connectReady: boolean;
  savedPaymentMethods: Array<{
    id: string;
    cardLast4: string;
    cardBrand: string | null;
    isDefault: boolean;
  }>;
};

export type CheckoutIntentResponse = {
  clientSecret: string | null;
  intentId: string;
  intentType: 'payment' | 'setup';
  publishableKey: string | null;
  stripeAccountId: string;
  amountCents: number;
  /** Development-only shop bypass — no Stripe Elements. */
  devBypass?: boolean;
};

export function buildPaymentSlotsFromPlayerSlots(
  playerSlots: Array<{
    guestCustomerTypeId?: string;
    customerTypeId?: string;
    products?: Array<{ totalPrice?: number }>;
  }>,
  fallbackCustomerTypeId?: string
): BookingPlayerSlotPaymentInput[] {
  return playerSlots
    .map((slot, index) => {
      const customerTypeId =
        slot.guestCustomerTypeId || slot.customerTypeId || fallbackCustomerTypeId;
      if (!customerTypeId) return null;
      const totalDollars = (slot.products ?? []).reduce(
        (sum, p) => sum + (Number(p.totalPrice) || 0),
        0
      );
      return {
        slotIndex: index,
        customerTypeId,
        amountCents: Math.round(totalDollars * 100),
      };
    })
    .filter((s): s is BookingPlayerSlotPaymentInput => s !== null);
}

export async function fetchPaymentRequirements(params: {
  courseId: string;
  teeTimeLocalIso: string;
  timeHHmm: string;
  customerId?: string;
  playerSlots: BookingPlayerSlotPaymentInput[];
}): Promise<PaymentRequirementsResponse> {
  const clubParams = publicBookingApiParamsObjectFromWindow();
  const response = await fetch(`${getApiBase()}/public/booking/payment-requirements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...clubParams, ...params }),
  });
  const body = await response.json();
  if (!response.ok) {
    if (body?.data) {
      return body.data as PaymentRequirementsResponse;
    }
    throw new Error(body.error || 'Failed to load payment requirements');
  }
  return body.data as PaymentRequirementsResponse;
}

export async function createCheckoutIntent(params: {
  courseId: string;
  teeTimeId: string;
  teeTimeLocalIso: string;
  timeHHmm: string;
  customerId: string;
  playerSlots: BookingPlayerSlotPaymentInput[];
}): Promise<CheckoutIntentResponse> {
  const clubParams = publicBookingApiParamsObjectFromWindow();
  const response = await fetch(`${getApiBase()}/public/booking/checkout-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...clubParams, ...params }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.error || 'Failed to start checkout');
  }
  return body.data as CheckoutIntentResponse;
}

export { onlinePaymentRequirementNeedsCheckout };
